import { Link, Button } from '@checkatrade/web-ui';
import { CreditCard } from '@cat-home-experts/mortar-iconography-web/CreditCard';
import { track } from 'lib/sdks/analytics';
import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

export function PaymentBadge({
  payment,
  location,
}: {
  payment?: { status?: string };
  location: string;
}) {
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [show, setShow] = useState(false);

  function close(event: MouseEvent) {
    if (buttonRef.current?.contains(event.target as Node)) return;
    if (tooltipRef.current?.contains(event.target as Node)) return;
    setShow(false);
  }

  useEffect(() => {
    if (!show) return;
    document.addEventListener('click', close);

    return () => document.removeEventListener('click', close);
  }, [show]);

  function toggle(event: MouseEvent) {
    event.preventDefault();
    setShow(prev => !prev);
  }

  if (payment?.status !== 'READY_FOR_PAYMENT') {
    return null;
  }

  function stopPropagation(event: ReactMouseEvent<HTMLDivElement>) {
    event.stopPropagation();
  }

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        onClick={toggle}
        variant="unstyled"
        className="peer flex min-h-min items-center gap-0.5 rounded-full border border-default-700 bg-default-200 fill-default-800 p-1 px-2 text-xs font-semibold text-default-800"
        aria-expanded={show}
        aria-controls={id}
      >
        <CreditCard color="" size={16} />
        Pay via Checkatrade
      </Button>
      <div
        onClick={stopPropagation}
        ref={tooltipRef}
        id={id}
        className="absolute top-9 z-10 hidden w-60 flex-col gap-2 rounded bg-default-900 p-2 text-xs text-white before:absolute before:inset-x-0 before:-top-1 before:-z-10 before:mx-auto before:size-4 before:rotate-45 before:bg-default-900 peer-aria-[expanded=true]:flex"
      >
        You can pay this tradesperson for any jobs you get done through
        Checkatrade
        <Link
          href="https://support.checkatrade.com/s/article/How-can-I-easily-pay-a-tradesperson-via-Checkatrade"
          className="font-semibold text-white underline"
          target="_blank"
          onClick={() => {
            track('global.payments_find_out_more', { location });
          }}
        >
          Find out more
        </Link>
      </div>
    </div>
  );
}
